import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import { getLpcProducts, getProducts } from '../../pages/api';
import { trackProductClick } from '../../utils/tracking';
import { useSiteSpect } from '../../utils/context/sitespect-provider';
import slugToSLI from '../../utils/slugToSLI';
import * as helpers from '../../organisms/algolia-plp/utils/helpers';
import searchClient from '../../utils/algolia';

import Typography from '../../atoms/typography';
import Carousel from '../../atoms/carousel';
import ProductCard from '../product-card';
import Picture from '../../atoms/picture';
import Ruler from '../../atoms/ruler';

import TaxonProductsAlgolia from './algolia';

const Container = styled.section`
  padding: 0 ${(props) => props.theme.modularScale.small};
  width: 100%;

  a {
    display: inline;
  }
`;

const NoSlider = styled.span`
  display: flex;
  flex-wrap: wrap;
  ${({ numChildren }) => (numChildren < 4 ? css`
    justify-content: center;
  ` : '')}
`;

const Title = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  text-align: center;
  a {
    font-family: inherit;
    text-decoration: none;
  }
`;

const Subtitle = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  font-family: ${(props) => props.theme.font.sans};
  letter-spacing: 0.2em;
  text-align: center;
  text-transform: uppercase;
`;

const Slide = styled(ProductCard)`
  margin-top: ${(props) => props.theme.modularScale['3xlarge']};
  outline: none;
  padding: 0 20px;

  a {
    text-decoration: none;
  }
`;

const Product = styled(ProductCard)`
  width: 50%;
  text-align: center;
  margin-top: 4rem;

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    width: 25%;
  }

  &.circle {
    figure {
      border-radius: 50%;
    }
  }
`;

const TaxonIcon = styled(Picture)`
  width: 50px;
  height: 50px;
`;

const TaxonProducts = (props) => {
  const path = props.data?.taxon_products_path ?? '';
  const sState = helpers.pathToSearchState(path);

  const [items, setItems] = useState([]);
  const [isSlider] = useState(props?.data?.taxon_products_is_slider);
  const [carouselRef, setCarouselRef] = useState(null);
  const { algolia = false } = useSiteSpect();

  const DEFAULT_PROPS = {
    searchClient,
    indexName: process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX
  };

  const onCarouselRef = useCallback((node) => {
    if (node !== null) {
      setCarouselRef(node);
    }
  }, []);

  const carouselSettings = {
    groupCells: true,
    prevNextButtons: true,
    pageDots: false
  };

  const productLimit = props.data?.taxon_products_limit ?? 4;

  useEffect(() => {
    const [pathNoQueries, queries] = path.split('?af=');
    const [firstPath, secondPath, ...cats] = pathNoQueries.split('/');
    const isLpc = firstPath.includes('lpc');
    if (!algolia) {
      const sliPath = () => {
        const withQueries = (filters) => {
          if (queries) {
            return `${queries} ${filters}`;
          }
          return filters;
        };

        const formatSlugs = (slugs) => {
          const newSlugs = slugs
            .map((p) => slugToSLI(p))
            .reduce((slugString, slug) => {
              const newString = slugString.length > 0
                ? [...slugString, `${slugString[slugString.length - 1]}_${slug}`]
                : [slug];
              return newString;
            }, []);
          const formattedFilters = newSlugs.reduce((allPaths, currentPath, index) => `${allPaths} cat${index + 1}:${currentPath}`, '');
          return withQueries(formattedFilters);
        };

        if (firstPath.includes('edit')) {
          return `edits:${slugToSLI(secondPath)} ${formatSlugs(cats)}`;
        }

        if (firstPath.includes('brand')) {
          return `brand:${slugToSLI(secondPath, true)} ${formatSlugs(cats)}`;
        }

        if (firstPath.includes('trend')) {
          return `trends:${slugToSLI(secondPath)} ${formatSlugs(cats)}`;
        }

        if (isLpc) {
          return formatSlugs(cats);
        }

        const paths = pathNoQueries.split('/').filter((s) => !/shop/.test(s));
        return formatSlugs(paths);
      };

      const getTaxonProducts = async (filters, count) => {
        const edit = isLpc
          ? await getLpcProducts({
            lpc: secondPath,
            params: {
              af: filters,
              cnt: count,
              srt: 0,
              w: '*'
            }
          })
          : await getProducts({
            params: {
              cnt: count,
              af: filters,
              srt: 0,
              w: '*'
            }
          });
        const editData = edit?.data ?? edit;
        return editData;
      };

      getTaxonProducts(sliPath(), productLimit)
        .then(({ results }) => setItems(results));
    }
  }, []);

  const setStyle = (styleName, index) => {
    if (styleName === 'both') {
      return index % 2 ? 'circle' : 'square';
    }
    return styleName;
  };

  const taxonProductWrapper = () => {
    const length = carouselRef?.selectedCells?.length ?? 1;

    if (isSlider && (items.length >= length)) {
      return (
        <Carousel
          flickityRef={onCarouselRef}
          type="taxon-products"
          options={carouselSettings}
          reloadOnUpdate
        >
          {
            items && items.map((item, i) => (
              <Slide
                taxonProducts
                key={item.rank}
                product={item}
                index={i + 1}
                showQuickShop={false}
                module="taxon_products"
                onClick={(e) => {
                  trackProductClick({ item });
                  const isWishlist = e.target.dataset.id === 'wishlist';

                  if (isWishlist) {
                    e.preventDefault();
                    return false;
                  }
                  return true;
                }}
              />
            ))
          }
        </Carousel>
      );
    }

    return (
      <NoSlider numChildren={items.length}>
        {
          items && items.map((item, i) => (
            <Product
              taxonProducts
              key={item.rank}
              product={item}
              index={i + 1}
              showQuickShop={false}
              module="taxon_products"
              className={setStyle(props.data.taxon_products_style ?? 'circle', i)}
              onClick={(e) => {
                trackProductClick({ item });
                const isWishlist = e.target.dataset.id === 'wishlist';

                if (isWishlist) {
                  e.preventDefault();
                  return false;
                }
                return true;
              }}
            />
          ))
        }
      </NoSlider>
    );
  };

  if (algolia) {
    return (
      <Container>
        <header>
          {props.data?.taxon_products_icon
            && (
              <TaxonIcon
                {...props.data.taxon_products_icon}
                alt={props.data?.taxon_products_icon_alt}
                circle
              />
            )}
          <Title element="h1" like="heading-4">
            {props.data.taxon_products_title_url ? (
              <Link href={props.data.taxon_products_title_url}>
                <a>
                  {props.data.taxon_products_title}
                </a>
              </Link>
            ) : props.data.taxon_products_title}
          </Title>
          <Subtitle element="p" like="paragraph-4">
            {props.data.taxon_products_subtitle}
          </Subtitle>
        </header>
        <TaxonProductsAlgolia
          {...DEFAULT_PROPS}
          searchState={sState}
          createURL={(state) => helpers.createURL(state, props)}
          isSlider={props.data.taxon_products_is_slider}
          style={props.data.taxon_products_style ?? 'circle'}
          productLimit={productLimit}
        />
        {props.data?.taxon_products_hr && <Ruler />}
      </Container>
    );
  }

  if (items && items.length > 0) {
    return (
      <Container>
        <header>
          {props.data?.taxon_products_icon
            && (
              <TaxonIcon
                {...props.data.taxon_products_icon}
                alt={props.data?.taxon_products_icon_alt}
                circle
              />
            )}
          <Title element="h1" like="heading-4">
            {props.data.taxon_products_title_url ? (
              <Link href={props.data.taxon_products_title_url}>
                <a>
                  {props.data.taxon_products_title}
                </a>
              </Link>
            ) : props.data.taxon_products_title}
          </Title>
          <Subtitle element="p" like="paragraph-4">
            {props.data.taxon_products_subtitle}
          </Subtitle>
        </header>
        {taxonProductWrapper()}
        {props.data?.taxon_products_hr && <Ruler />}
      </Container>
    );
  }

  return (
    <Container dangerouslySetInnerHTML={{ __html: `<!-- no taxon items for "${props.data?.taxon_products_path ?? '(no path specified)'}" -->` }} />
  );
};

TaxonProducts.propTypes = {
  data: PropTypes.object.isRequired
};

export default TaxonProducts;
